import React, { useEffect, useState } from "react";
import { Wallet, Loader2, RefreshCw, TrendingUp, Cpu, Package, Image as ImageIcon, Video } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const PERIODS = [{ id: "day", label: "اليوم" }, { id: "month", label: "هذا الشهر" }, { id: "year", label: "هذه السنة" }];
const KIND = { image: "صور", video: "فيديو حقيقي", animation: "فيديو أنيميشن", premium: "فيديو مميز" };
const pct = (v) => <span className={v >= 0 ? "text-emerald-400" : "text-rose-400"}>{v}%</span>;

const Economics = ({ e }) => (
  <div className="grid lg:grid-cols-2 gap-4" data-testid="finance-economics">
    <div className="rounded-2xl border border-border p-4 overflow-x-auto">
      <div className="text-sm font-bold mb-2">ربحية كل عملية (الدفع حسب الطلب)</div>
      <table className="w-full text-xs"><thead><tr className="text-muted-foreground">
        <th className="text-start py-1">النوع</th><th>السعر</th><th>الصافي</th><th>التكلفة</th><th>الربح</th><th>الهامش</th></tr></thead>
        <tbody>{e.units.map((u) => (
          <tr key={u.kind} className="border-t border-border" data-testid={`econ-unit-${u.kind}`}>
            <td className="py-1.5">{u.label}</td><td className="text-center">{u.price_sar}</td><td className="text-center">{u.net_sar}</td>
            <td className="text-center text-rose-400">{u.cost_sar}</td><td className="text-center font-bold">{u.profit_sar}</td><td className="text-center">{pct(u.margin_pct)}</td>
          </tr>))}</tbody></table>
    </div>
    <div className="rounded-2xl border border-border p-4 overflow-x-auto">
      <div className="text-sm font-bold mb-2">ربحية الباقات عند الاستهلاك الكامل</div>
      <table className="w-full text-xs"><thead><tr className="text-muted-foreground">
        <th className="text-start py-1">الباقة</th><th>السعر</th><th>صور/فيديو</th><th>أقصى تكلفة</th><th>الربح</th><th>الهامش</th></tr></thead>
        <tbody>{e.plans.map((p) => (
          <tr key={p.id} className="border-t border-border" data-testid={`econ-plan-${p.id}`}>
            <td className="py-1.5">{p.name}</td><td className="text-center">{p.price_sar}</td><td className="text-center">{p.images}/{p.videos}</td>
            <td className="text-center text-rose-400">{p.max_cost_sar}</td><td className="text-center font-bold">{p.profit_sar}</td><td className="text-center">{pct(p.margin_pct)}</td>
          </tr>))}</tbody></table>
    </div>
    <p className="lg:col-span-2 text-[11px] text-muted-foreground">المبالغ بالريال. الصافي بعد ضريبة القيمة المضافة {e.vat_pct}% ورسوم الدفع {e.fee_pct}%. الأنيميشن بنفس تكلفة الفيديو الذكي تقريباً.</p>
  </div>
);

const sar = (v) => `${Number(v || 0).toLocaleString("ar-SA", { maximumFractionDigits: 2 })} ر.س`;

const Box = ({ label, value, sub, tone = "", testid }) => (
  <div className="border border-border rounded-xl px-4 py-3" data-testid={testid}>
    <div className={`text-xl font-extrabold ${tone}`}>{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const PeriodRow = ({ id, label, p }) => (
  <div className="rounded-2xl border border-border p-4 space-y-3" data-testid={`finance-period-${id}`}>
    <div className="font-bold text-sm text-accent">{label}</div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Box label="الإيرادات" value={sar(p.revenue_sar)} tone="text-emerald-400" testid={`finance-revenue-${id}`} />
      <Box label="تكلفة الذكاء الاصطناعي" value={sar(p.ai_sar)} sub={`Runway $${p.runway_usd} + نصوص ≈ $${p.text_usd}`} tone="text-rose-400" testid={`finance-cost-${id}`} />
      <Box label="صافي الربح" value={sar(p.net_sar)} tone={p.net_sar >= 0 ? "text-emerald-400" : "text-rose-400"} testid={`finance-net-${id}`} />
      <Box label="باقات مباعة" value={p.sold} sub={`منها مجانية بكوبون: ${p.free_coupon}`} testid={`finance-sold-${id}`} />
    </div>
    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
      <span className="px-2 py-1 rounded-full bg-secondary">صور: {p.images}</span>
      <span className="px-2 py-1 rounded-full bg-secondary">فيديو: {p.videos}</span>
      <span className="px-2 py-1 rounded-full bg-secondary">نصوص واستراتيجيات: {p.texts}</span>
      {Object.entries(p.by_plan || {}).map(([name, v]) => (
        <span key={name} className="px-2 py-1 rounded-full bg-primary/15 text-primary">{name}: {v.count} ({sar(v.revenue_sar)})</span>
      ))}
      {Object.entries(p.by_kind || {}).map(([k, v]) => (
        <span key={k} className={`px-2 py-1 rounded-full ${v.profit_sar >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`} data-testid={`finance-kind-${id}-${k}`}>
          {KIND[k] || k}: {v.count} · تكلفة {sar(v.cost_sar)} · قيمة {sar(v.value_sar)} · ربح {sar(v.profit_sar)}
        </span>
      ))}
      {p.test_topups_sar > 0 && <span className="px-2 py-1 rounded-full bg-amber-500/15 text-amber-400">شحن محفظة (تجريبي): {sar(p.test_topups_sar)}</span>}
    </div>
  </div>
);

export function AdminFinance() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/finance").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="glass rounded-2xl p-6 space-y-5" data-testid="admin-finance">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-accent" /><h2 className="text-lg font-bold">الإيرادات وتكلفة الذكاء الاصطناعي</h2></div>
        <Button size="sm" variant="outline" onClick={load} className="border-border gap-1" data-testid="finance-refresh"><RefreshCw className="w-3.5 h-3.5" /> تحديث</Button>
      </div>
      {!d ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
        <>
          <div className="grid sm:grid-cols-3 gap-3" data-testid="finance-unit-costs">
            <Box label="تكلفة الصورة الواحدة" value={sar(d.unit.image_sar)} sub={`${d.unit.image_credits} كريدت · $${d.unit.image_usd}`} />
            <Box label="تكلفة الفيديو (15 ث)" value={sar(d.unit.video_sar)} sub={`${d.unit.video_credits} كريدت · $${d.unit.video_usd}`} />
            <Box label="تكلفة النص (تقديرية)" value={`$${d.unit.text_usd}`} sub={`الاستراتيجية ≈ $${d.unit.strategy_usd}`} />
          </div>
          {d.economics && <Economics e={d.economics} />}
          {PERIODS.map((x) => <PeriodRow key={x.id} id={x.id} label={x.label} p={d.periods[x.id]} />)}
          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2 text-sm font-bold"><Cpu className="w-4 h-4 text-primary" /> آخر عمليات التوليد وتكلفتها</div>
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="finance-recent-ops">
                {d.recent.length === 0 ? <p className="text-xs text-muted-foreground">لا توجد عمليات بعد.</p> : d.recent.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-xs border border-border rounded-lg px-3 py-2">
                    <span className="flex items-center gap-2 truncate">{r.type === "video" ? <Video className="w-3.5 h-3.5 text-accent" /> : <ImageIcon className="w-3.5 h-3.5 text-primary" />}<span className="truncate max-w-[160px]">{r.title || "—"}</span></span>
                    <span className="text-muted-foreground">{(r.created_at || "").slice(0, 10)}</span>
                    <span className="font-bold">{sar(r.cost_sar)} <span className="text-muted-foreground font-normal">({r.credits} كريدت)</span></span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2 text-sm font-bold"><Package className="w-4 h-4 text-accent" /> آخر الباقات المباعة</div>
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="finance-recent-sales">
                {d.recent_sales.length === 0 ? <p className="text-xs text-muted-foreground">لا توجد مبيعات بعد.</p> : d.recent_sales.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-xs border border-border rounded-lg px-3 py-2">
                    <span className="truncate max-w-[150px]">{s.email}</span>
                    <span className="text-primary">{s.plan_name}</span>
                    <span className="font-bold">{s.free_coupon ? <span className="text-amber-400">مجانية بكوبون</span> : sar(s.final_price)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Wallet className="w-3 h-3" /> شحن المحفظة تجريبي ولا يُحتسب ضمن الإيرادات حتى ربط بوابة الدفع. تكلفة النصوص تقديرية.</p>
        </>
      )}
    </div>
  );
}
